// =====================================================================
// ReportCard (S39) — card individual de um relatorio diario na lista.
// Mostra: data, badges de status, KPIs preview, botoes acao.
// =====================================================================
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import RefreshIcon from "@material-ui/icons/Refresh";
import GetAppIcon from "@material-ui/icons/GetApp";
import { downloadDailyReport } from "../../../services/dailyReportsService";

const useStyles = makeStyles((theme) => ({
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.15s, box-shadow 0.15s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
    }
  },
  dateRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  dateText: {
    fontWeight: 700,
    fontSize: "1.05rem"
  },
  kpisGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5)
  },
  kpi: {
    textAlign: "center",
    padding: theme.spacing(0.75),
    background: "rgba(0,0,0,0.03)",
    borderRadius: 6
  },
  kpiValue: {
    fontWeight: 700,
    fontSize: "1rem"
  },
  kpiLabel: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.04em"
  },
  highlight: {
    marginTop: theme.spacing(1.5),
    fontSize: "0.85rem",
    color: theme.palette.text.secondary
  },
  actions: {
    justifyContent: "space-between",
    padding: theme.spacing(1, 2, 2)
  },
  statusFailed: {
    color: theme.palette.error.main
  }
}));

const WEEKDAYS_PT = [
  "Domingo",
  "Segunda-feira",
  "Terca-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sabado"
];

const formatDate = (isoDate) => {
  // 'YYYY-MM-DD' -> 'DD/MM (Dia da semana)'
  if (!isoDate) return "—";
  const d = new Date(`${isoDate}T12:00:00Z`);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  const weekday = WEEKDAYS_PT[d.getUTCDay()];
  return `${dd}/${mm}/${yyyy} (${weekday.slice(0, 3)})`;
};

const ReportCard = ({ report, onOpen, canRegenerate, onRegenerate }) => {
  const classes = useStyles();
  const kpis = report.kpis_summary || {};
  const isFailed = report.generation_status === "failed";

  const handleDownload = (e) => {
    e.stopPropagation();
    downloadDailyReport(
      report.id,
      `relatorio_clee_${report.report_date}.html`
    ).catch(() => {
      // toast handled upstream
    });
  };

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent style={{ flex: 1, cursor: "pointer" }} onClick={onOpen}>
        <Box className={classes.dateRow}>
          <Typography className={classes.dateText}>
            {formatDate(report.report_date)}
          </Typography>
          {isFailed ? (
            <Chip
              size="small"
              label="FALHOU"
              className={classes.statusFailed}
              style={{ background: "#fee", color: "#c00", fontWeight: 700 }}
            />
          ) : (
            <Chip
              size="small"
              label="OK"
              style={{ background: "#dcfce7", color: "#065f46", fontWeight: 700 }}
            />
          )}
        </Box>

        {isFailed ? (
          <Typography variant="body2" color="error">
            Erro na geracao: {report.generation_error || "—"}
          </Typography>
        ) : (
          <>
            <Box className={classes.kpisGrid}>
              <Box className={classes.kpi}>
                <div className={classes.kpiValue}>{kpis.msgs_total || 0}</div>
                <div className={classes.kpiLabel}>Mensagens</div>
              </Box>
              <Box className={classes.kpi}>
                <div className={classes.kpiValue}>
                  {kpis.appointments_count || 0}
                </div>
                <div className={classes.kpiLabel}>Consultas</div>
              </Box>
              <Box className={classes.kpi}>
                <div className={classes.kpiValue}>
                  {kpis.tickets_closed || 0}
                </div>
                <div className={classes.kpiLabel}>Fechados</div>
              </Box>
            </Box>

            <Box className={classes.highlight}>
              {kpis.top_attendant && (
                <div>
                  ⭐ Destaque:{" "}
                  <strong>{kpis.top_attendant}</strong>
                </div>
              )}
              {kpis.pending_patients > 0 && (
                <div>
                  ⏳ {kpis.pending_patients} pacientes esperando
                </div>
              )}
              {kpis.alerts_urgentes > 0 && (
                <div style={{ color: "#c00" }}>
                  🚨 {kpis.alerts_urgentes} alerta(s) urgente(s)
                </div>
              )}
            </Box>
          </>
        )}
      </CardContent>

      <CardActions className={classes.actions}>
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={onOpen}
          color="primary"
        >
          Ver
        </Button>
        <Button size="small" startIcon={<GetAppIcon />} onClick={handleDownload}>
          Baixar
        </Button>
        {canRegenerate && (
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={(e) => {
              e.stopPropagation();
              if (onRegenerate) onRegenerate();
            }}
          >
            Regenerar
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default ReportCard;
